import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';
import './UtilityServiceTemplateSettings.css';

const DEFAULT_QUICK_REPLIES = [
  'Book Appointment',
  'Update Profile',
  'Leave Feedback',
  'View Catalogue'
];

const DEFAULT_TEMPLATE_NAME = 'Utility Service';
const DEFAULT_TEMPLATE_DESCRIPTION = 'Single utility template with common service options';
const DEFAULT_TEMPLATE_BODY = [
  'Hello! \n\nWe are here to assist you with our services. Please select one of the options below to proceed.',
  
].join('\n');

const getBodyFromTemplate = (template) => {
  const bodyComponent = (template?.waComponents || []).find((component) => component.type === 'BODY');
  return bodyComponent?.text || '';
};

const getQuickRepliesFromTemplate = (template) => {
  const buttonsComponent = (template?.waComponents || []).find((component) => component.type === 'BUTTONS');
  const quickReplies = (buttonsComponent?.buttons || [])
    .filter((button) => button?.type === 'QUICK_REPLY')
    .map((button) => String(button.text || '').trim())
    .filter(Boolean);

  return quickReplies.length > 0 ? quickReplies : [...DEFAULT_QUICK_REPLIES];
};

const UtilityServiceTemplateSettings = () => {
  const MAX_QUICK_REPLIES = 10;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const authUser = useSelector(state => state.auth?.user || {});
  const { currentAccount, accounts } = useSelector(state => state.businessAccounts || {});
  const activeWabaId = currentAccount?.wabaId || accounts?.[0]?.wabaId || authUser?.wabaId || '';

  const [formData, setFormData] = useState({
    name: DEFAULT_TEMPLATE_NAME,
    description: DEFAULT_TEMPLATE_DESCRIPTION,
    body: DEFAULT_TEMPLATE_BODY,
    quickReplies: [...DEFAULT_QUICK_REPLIES]
  });
  const [formErrors, setFormErrors] = useState({});

  const setFormFromTemplate = useCallback((record) => {
    setFormData({
      name: DEFAULT_TEMPLATE_NAME,
      description: record?.description || DEFAULT_TEMPLATE_DESCRIPTION,
      body: getBodyFromTemplate(record) || DEFAULT_TEMPLATE_BODY,
      quickReplies: getQuickRepliesFromTemplate(record)
    });
    setFormErrors({});
  }, []);

  const loadTemplates = useCallback(async () => {
    if (!activeWabaId) return;

    setLoading(true);
    try {
      const response = await apiService.templateSettings.getUtilityServiceTemplates({ wabaId: activeWabaId });
      const records = response.data?.data || [];

      if (records.length > 0) {
        setTemplate(records[0]);
        setFormFromTemplate(records[0]);
        return;
      }

      // Lazy-create mode: show default form first, create only on Save.
      setTemplate(null);
      setFormFromTemplate(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load utility service templates');
    } finally {
      setLoading(false);
    }
  }, [activeWabaId, setFormFromTemplate]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleAddQuickReply = () => {
    setFormData((prev) => {
      if (prev.quickReplies.length >= MAX_QUICK_REPLIES) {
        return prev;
      }
      return {
        ...prev,
        quickReplies: [...prev.quickReplies, '']
      };
    });
  };

  const handleRemoveQuickReply = (indexToRemove) => {
    setFormData((prev) => {
      if (prev.quickReplies.length <= 1) {
        return prev;
      }
      return {
        ...prev,
        quickReplies: prev.quickReplies.filter((_, index) => index !== indexToRemove)
      };
    });
  };

  const handleQuickReplyChange = (indexToEdit, value) => {
    setFormData((prev) => ({
      ...prev,
      quickReplies: prev.quickReplies.map((item, index) => (index === indexToEdit ? value : item))
    }));
  };

  const validateForm = () => {
    const errors = {};
    const quickReplies = formData.quickReplies.map((item) => String(item || '').trim()).filter(Boolean);

    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    }

    if (!formData.body.trim()) {
      errors.body = 'Body text is required';
    } else if (formData.body.trim().length > 1024) {
      errors.body = 'Body text must be 1024 characters or fewer';
    }

    if (quickReplies.length === 0) {
      errors.quickReplies = 'At least one quick reply is required';
    } else if (quickReplies.length > MAX_QUICK_REPLIES) {
      errors.quickReplies = `A maximum of ${MAX_QUICK_REPLIES} quick replies is allowed`;
    }

    const duplicateSet = new Set();
    quickReplies.forEach((replyText) => {
      if (replyText.length > 25) {
        errors.quickReplies = 'Each quick reply must be 25 characters or fewer';
      }
      const key = replyText.toLowerCase();
      if (duplicateSet.has(key)) {
        errors.quickReplies = 'Quick reply text must be unique';
      }
      duplicateSet.add(key);
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveTemplate = async (event) => {
    event.preventDefault();
    if (!validateForm() || !activeWabaId) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        wabaId: activeWabaId,
        templateType: 'UTILITY_SERVICE',
        templateId: 'utility-service',
        name: DEFAULT_TEMPLATE_NAME,
        description: formData.description.trim(),
        body: formData.body.trim(),
        quickReplies: formData.quickReplies.map((item) => String(item || '').trim()).filter(Boolean),
        language: 'en'
      };

      if (template?._id) {
        await apiService.templateSettings.updateUtilityServiceTemplate(template._id, payload);
        toast.success('Utility service template updated');
      } else {
        const createResponse = await apiService.templateSettings.createUtilityServiceTemplate(payload);
        const createdRecord = createResponse.data?.data || null;
        setTemplate(createdRecord);
        setFormFromTemplate(createdRecord);
        toast.success('Utility service template created');
      }

      if (template?._id) {
        const refreshedResponse = await apiService.templateSettings.getUtilityServiceTemplates({ wabaId: activeWabaId });
        const refreshedRecord = (refreshedResponse.data?.data || [])[0] || null;
        setTemplate(refreshedRecord);
        setFormFromTemplate(refreshedRecord);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save utility service template');
    } finally {
      setSaving(false);
    }
  };

  if (!activeWabaId) {
    return (
      <div className="settings-form">
        <div className="empty-state">
          <div className="empty-icon">⚠️</div>
          <p>No WhatsApp Business Account found</p>
          <span className="help-text">Connect a WABA account before creating utility service templates.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-form utility-service-settings">
      {loading ? (
        <p>Loading utility service templates...</p>
      ) : (
        <form className="utility-template-form" onSubmit={handleSaveTemplate}>
          <div className="utility-template-form-header">
            <div>
              <h3>Utility Service Template</h3>
              <p>Single template configuration with fixed service options.</p>
            </div>
            <span className="utility-template-badge">UTILITY</span>
          </div>

          <div className="utility-template-status-row">
            <span className={`utility-template-status ${template?.status || 'PENDING'}`}>
              Status: {template?.status || 'PENDING'}
            </span>
            {!template?._id && (
              <span className="help-text">Template will be created when you click Save.</span>
            )}
          </div>

          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              value={formData.name}
              readOnly
              disabled
              placeholder="Utility Service"
            />
            <span className="help-text">Template name is fixed and cannot be changed.</span>
            {formErrors.name && <span className="error-text">{formErrors.name}</span>}
          </div>

          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Single utility template with common service options"
            />
          </div>

          <div className="form-group">
            <label>Body Text *</label>
            <textarea
              rows="7"
              value={formData.body}
              onChange={(event) => setFormData((prev) => ({ ...prev, body: event.target.value }))}
              placeholder="Hello! Please choose one option below so we can help you quickly..."
            />
            {formErrors.body && <span className="error-text">{formErrors.body}</span>}
          </div>

          <div className="form-group">
            <label>Quick Reply Options</label>
            <div className="quick-reply-editor-list">
              {formData.quickReplies.map((quickReply, index) => (
                <div className="quick-reply-editor-item" key={`${index}-${quickReply}`}>
                  <input
                    type="text"
                    value={quickReply}
                    maxLength={25}
                    onChange={(event) => handleQuickReplyChange(index, event.target.value)}
                    placeholder={`Quick reply ${index + 1}`}
                  />
                  <button
                    type="button"
                    className="btn-remove-quick-reply"
                    onClick={() => handleRemoveQuickReply(index)}
                    disabled={formData.quickReplies.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="quick-reply-editor-actions">
              <button
                type="button"
                className="btn-add-quick-reply"
                onClick={handleAddQuickReply}
                disabled={formData.quickReplies.length >= MAX_QUICK_REPLIES}
              >
                Add Option
              </button>
              <span className="help-text">You can add, edit, or remove quick reply options.</span>
            </div>
            {formErrors.quickReplies && <span className="error-text">{formErrors.quickReplies}</span>}
          </div>

          <div className="utility-template-form-actions">
            <button type="submit" className="btn-save-utility-template" disabled={saving}>
              {saving ? 'Saving...' : 'Save Utility Template'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default UtilityServiceTemplateSettings;
